import React from "react";
import { createMRTColumnHelper, MRT_ColumnDef } from "material-react-table";
import { Box } from "@mui/material";
import { VersionTeamConnection, RevisionItem } from "../../types/versionTeam";
import EntityReferenceComponent from "../../components/entitiesReferences/EntityReferenceComponent";
import { IntellexusDateStructure } from "../../types/intellexusDate";
import {
  CONNECTION_FROM_COLUMN_ID,
  CONNECTION_TO_COLUMN_ID,
  EntityWithMetadataTableSuffixColumns,
  createConnectionFilterSpec,
} from "./EntityUtils";
import type { FilterSpec } from "../../hooks/useServerFilters";
import { GetIntellexusDate } from "../../components/forms/common/intellexusDate/IntellexusDateField";

const columnHelper = createMRTColumnHelper<VersionTeamConnection>();

const RevisionCell: React.FC<{ value: RevisionItem[] }> = ({ value }) => {
  if (!Array.isArray(value) || value.length === 0) return <Box>-</Box>;

  return (
    <>
      {value.map((item, index) => {
        const revisionText = item?.revision ?? "-";
        return (
          <React.Fragment key={index}>
            {index > 0 && ", "}
            {revisionText}
          </React.Fragment>
        );
      })}
    </>
  );
};

const baseVersionTeamColumns: MRT_ColumnDef<VersionTeamConnection, any>[] = [
  columnHelper.accessor((row) => row.fromDoc?.published_id ?? row._from, {
    id: CONNECTION_FROM_COLUMN_ID,
    header: "From",
    enableColumnFilter: false,
    Cell: ({ row }) => (
      <EntityReferenceComponent entity={row.original.fromDoc} />
    ),
  }),

  columnHelper.accessor((row) => row.toDoc?.published_id ?? row._to, {
    id: CONNECTION_TO_COLUMN_ID,
    header: "To Team",
    enableColumnFilter: false,
    Cell: ({ row }) => <EntityReferenceComponent entity={row.original.toDoc} />,
  }),

  columnHelper.accessor("system_team_contribution_type", {
    header: "Team Contribution Type",
  }),

  columnHelper.accessor("date", {
    header: "Date",
    Cell: ({ cell }) => {
      const iDate = cell.getValue<IntellexusDateStructure | undefined>();
      return iDate ? GetIntellexusDate(iDate) : <Box>-</Box>;
    },
  }),
];
const tibetanExtraColumns: MRT_ColumnDef<VersionTeamConnection, any>[] = [
  columnHelper.accessor("name", {
    header: "Name",
    Cell: ({ cell }) => cell.getValue() || "-",
  }),
  columnHelper.accessor("place_identification_status", {
    header: "Place Identification Status",
    Cell: ({ cell }) => {
      const value = cell.getValue<string | undefined>();
      return value ? value : <Box>-</Box>;
    },
  }),

  columnHelper.accessor("revision", {
    header: "Revisions",
    Cell: ({ cell }) => <RevisionCell value={cell.getValue<any[]>()} />,
  }),

  columnHelper.accessor("place", {
    header: "Place",
    enableColumnFilter: false,
    Cell: ({ cell }) =>
      cell.getValue() ? (
        <EntityReferenceComponent entity={cell.getValue()} />
      ) : (
        "-"
      ),
  }),
];
const chineseExtraColumns: MRT_ColumnDef<VersionTeamConnection, any>[] = [
  columnHelper.accessor("title", {
    header: "Title",
    Cell: ({ cell }) => cell.getValue() || "-",
  }),
  columnHelper.accessor("taisho_catalog_no", {
    header: "Taisho Catalog No",
    Cell: ({ cell }) => cell.getValue() || "-",
  }),
  columnHelper.accessor("source_language", {
    header: "Source Language",
    Cell: ({ cell }) => cell.getValue() || "-",
  }),
];

export const versionTeamColumns = [
  ...baseVersionTeamColumns,
  ...EntityWithMetadataTableSuffixColumns,
];

export const tibetanVersionTeamColumns = [
  ...baseVersionTeamColumns,
  ...tibetanExtraColumns,
  ...EntityWithMetadataTableSuffixColumns,
];

export const chineseVersionTeamColumns = [
  ...baseVersionTeamColumns,
  ...chineseExtraColumns,
  ...EntityWithMetadataTableSuffixColumns,
];

export const versionTeamFilterSpec: FilterSpec = createConnectionFilterSpec({
  system_team_contribution_type: { kind: "string" },
  date: { kind: "string" },
  name: { kind: "string" },
  place_identification_status: { kind: "string" },
  revision: { kind: "string" },
  title: { kind: "string" },
  taisho_catalog_no: { kind: "string" },
  source_language: { kind: "string" },
});
